import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useSearchParams } from 'react-router';

import { Skin } from '@sorare/core/src/__generated__/globalTypes';
import { SEARCH_PARAMS } from '@sorare/core/src/components/search/InstantSearch/types';
import { formatSearchString } from '@sorare/core/src/lib/string/formatSearchString';

import { useLocalStorage } from '@sorare/core/src/hooks/useLocalStorage.ts';
import type { useGallerySearchResults_cardCollection } from './__generated__/useGallerySearch.graphql.ts';

const isSkin = (skin: string | undefined): skin is Skin => {
  return Object.values(Skin).includes(skin as Skin);
};

export const useGallerySearch = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get(SEARCH_PARAMS.QUERY) || '';
  const skin = searchParams.get('skin') || undefined;
  const typedSkin = isSkin(skin) ? skin : undefined;
  const [showAll, setShowAll] = useLocalStorage(
    'enableShowAllCardSlots',
    false
  );
  const updateParams = (updates: Record<string, string | undefined>) => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
        });
        return next;
      },
      { replace: true }
    );
  };

  return {
    search,
    skin: typedSkin,
    showAll,
    onSearchChange: (newSearch: string) => {
      updateParams({ [SEARCH_PARAMS.QUERY]: newSearch || undefined });
    },
    onSkinChange: (newSkin: Skin | undefined) => {
      updateParams({ skin: newSkin });
    },
    onShowAllChange: (newShowAll: boolean) => {
      setShowAll(newShowAll);
    },
  };
};

export const useGallerySearchResults = <
  T extends useGallerySearchResults_cardCollection,
>(
  search: string,
  cardCollections: Nullable<T[]>
): {
  hitType: 'collection' | 'player';
  cardCollection: T;
}[] => {
  if (!search.trim()) {
    return (
      cardCollections?.map(cardCollection => ({
        hitType: 'collection',
        cardCollection,
      })) || []
    );
  }
  return (
    cardCollections
      ?.map(
        (
          cardCollection
        ): { hitType: 'collection' | 'player'; cardCollection: T } | null => {
          const hasSearchInput = !!search.trim();
          const collectionNameHit = [
            cardCollection.anyTeam?.shortName,
            cardCollection.anyTeam?.name,
            cardCollection.anyTeam?.code,
          ].some(token =>
            formatSearchString(token)?.includes(
              formatSearchString(search) || ''
            )
          );
          const playerNameHit = cardCollection.userCardCollection?.slots?.some(
            slot =>
              formatSearchString(slot.slot?.anyPlayer?.displayName)?.includes(
                formatSearchString(search) || ''
              )
          );

          if (hasSearchInput && !collectionNameHit && !playerNameHit) {
            return null;
          }
          return {
            hitType: collectionNameHit ? 'collection' : 'player',
            cardCollection,
          };
        }
      )
      .filter(Boolean) || []
  );
};

useGallerySearchResults.fragments = {
  cardCollection: gql`
    fragment useGallerySearchResults_cardCollection on CardCollection {
      slug
      anyTeam {
        slug
        shortName
        name
        code
      }
      userCardCollection(forUserSlug: $user) {
        id
        slots(skin: $skin) {
          slug
          slot {
            id
            anyPlayer {
              slug
              displayName
            }
          }
        }
      }
    }
  ` as TypedDocumentNode<useGallerySearchResults_cardCollection>,
};
